/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadRoutes as getRoutes} from '@ion/core/src/services/Connect';
import GroupProperties from '@ion/core/src/services/GroupProperties';
import {LoadRoutesParams} from '@ion/core/src/types/services';
import {FilterOption, FilterPropsType} from '../types/types';
import Filter from './Filter';
import {createFilterOptions, getMenuStyles} from './FilterHelpers';
import {FilterParamsProps} from './useFilterSubmit';

/**
 * Defines the prop definitions for RouteFilter ...
 *
 * accountId:      The ID of currently selected account.
 * loadRoutes: Override function to load the default options data.
 */
export interface RoutFilterProps extends FilterPropsType {
  accountId: number;
  loadRoutes?: Function;
};

/**
 * The RouteFilter component is a reusable filter for managing the filtering
 * of route categories, and can be custom-styled with props.
 *
 * ```typescript
 *
 * import RouteFilter from '@ion/dls/src/filters';
 *
 * <RouteFilter elementId={elementId}
 *   name={name}
 *   accountId={accountId}
 *   filterState={filterState}
 *   loadRoutes={loadRoutes}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 *   onMenuClose={onMenuClose}
 *   primaryButtonDisabled={primaryButtonDisabled}
 *   secondaryButtonDisabled={secondaryButtonDisabled}
 *   showSelectedOptionsCount={false}
 *   isMobileView= {false} />
 *
 * ```
 */
export const RouteFilter = ({
  elementId,
  name,
  accountId,
  loadRoutes,
  onSubmit,
  onCancel,
  onMenuClose,
  primaryButtonDisabled,
  secondaryButtonDisabled,
  filterState = {},
  showSelectedOptionsCount = true,
  isMobileView = false
}: RoutFilterProps) => {
  const {
    loading: isLoading,
    error: hasErrors,
    selected: selectedOptions
  }                                         = filterState;
  const {
    menuLeft,
    menuWidth,
    contentGridTemplateColumns,
    contentJustifyContent,
    contentGridColumnGap,
    contentGridRowGap
  } = getMenuStyles(isMobileView);

  function handleSubmit({selections, options, loading, hasError}) {
    const filterParams: FilterParamsProps = {
      optionsKey        : FilterOption.name,
      selectionsKey     : FilterOption.id,
      augmentSelections : selected => ({
        id   : Number(selected?.[FilterOption.id]),
        name : selected[FilterOption.name]
      }),
      options,
      selections,
      loading,
      hasError
    };
    onSubmit?.(name, filterParams);
  };

  const loadFilterOptions = async function() {
    if (!loadRoutes) {
      await GroupProperties.getInheritedProperties(accountId);
    }
    const data: LoadRoutesParams = {
      accountId,
      pageId   : 1,
      pageSize : 1000,
      sortOn   : ['+name']
    };
    const {routes} = await (loadRoutes || getRoutes)(data);
    return {
      result       : routes,
      filterResult : routes
    };
  };

  return (
    <Filter optionType={translate('common:filters.route')}
      optionPropId={FilterOption?.id}
      optionPropLabel={FilterOption?.name}
      selectedOptionIds={selectedOptions?.map(option => option?.id ?? option)}
      loading={isLoading}
      showSelectedOptionsCount={showSelectedOptionsCount}
      primaryButtonDisabled={primaryButtonDisabled}
      secondaryButtonDisabled={secondaryButtonDisabled}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      onMenuClose={onMenuClose}
      elementId={elementId}
      menuLeft={menuLeft}
      menuWidth={menuWidth}
      contentGridTemplateColumns={contentGridTemplateColumns}
      contentJustifyContent={contentJustifyContent}
      contentGridColumnGap={contentGridColumnGap}
      contentGridRowGap={contentGridRowGap}
      loadFilterOptions={loadFilterOptions}
      accountId={accountId}
      isMobileView={isMobileView} />
  );
};

export default RouteFilter;
